import { Icon } from '../icon';
import { SidebarButton, SidebarItem, SidebarItemIdType } from '../layouts';
import { ZTooltip } from '../ztooltip';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { T } from 'src/shared/helpers';
import SearchIcon from 'src/shared/icons/search-solid.svg';
import TimesSolidIcon from 'src/shared/icons/times-solid.svg';
import styled, { useTheme } from 'styled-components';
import { CollapsibleTreeItems, SidebarTreeItem } from './collapsibleTreeItems';
import {
    SidebarButtonStyle,
    SidebarButtons,
    SidebarContainer,
    SidebarHeader,
    SidebarHeaderContainer,
    SidebarListItem,
    SidebarSearchInput,
    StyledSpan,
    TooltipCategories
} from './commonStyleSideBar';

export const TreeSidebarIcon = styled(Icon)<{ $isActive?: boolean }>`
    ${(props) =>
        props.$isActive &&
        `
        color: ${props.theme.app.secondary};
    `}
`;

interface SidebarProps<T extends SidebarItemIdType = string> {
    position?: 'absolute' | 'relative' | 'fixed' | 'static' | 'sticky';
    sidebarHeader?: string;
    sidebarHeaderButtons?: SidebarButton[];
    sidebarAllowSearch?: boolean;
    isDialog?: boolean;
    showTooltip?: boolean;
    className?: string;
    sidebarItems: SidebarItem<T>[];
    maxWidthItems?: string;
    onSidebarItemClick: (item: SidebarItem<T>) => void;
    onSidebarItemButtonClick?: (button: SidebarButton, item: SidebarItem<T>) => void;
    onSidebarHeaderButtonClick?: (button: SidebarButton) => void;
    onSidebarSubItemButtonClick?: (button: SidebarButton, item: SidebarItem<T>) => void;
}

export const TreeSidebar = <T extends SidebarItemIdType = string>({
    position,
    sidebarHeader,
    sidebarAllowSearch,
    sidebarHeaderButtons,
    sidebarItems,
    isDialog,
    showTooltip = true,
    className,
    maxWidthItems,
    onSidebarItemClick,
    onSidebarItemButtonClick,
    onSidebarSubItemButtonClick,
    onSidebarHeaderButtonClick
}: PropsWithChildren<SidebarProps<T>>) => {
    const [isSearchMode, setSearchMode] = useState(false);
    const [filter, setFilter] = useState('');

    const theme = useTheme();

    const items = useMemo(
        () =>
            sidebarItems.filter(
                (x) =>
                    x.label.toLowerCase().indexOf(filter.toLowerCase()) !== -1 ||
                    (x.subItems != undefined && x.subItems.some((category) => category.label.toLowerCase().indexOf(filter.toLowerCase()) !== -1))
            ),
        [filter, sidebarItems]
    );

    const macrocategories: SidebarTreeItem[] = items.map((x) => {
        const subCategories: SidebarTreeItem[] =
            x.subItems == null
                ? []
                : x.subItems
                      .filter((x) => x.label.toLowerCase().indexOf(filter) !== -1)
                      .map((x) => {
                          let categoryNode = (
                              <SidebarListItem key={x.id.toString()} $selected={x.selected} disabled={x.disabled} onClick={() => onSidebarItemClick(x)}>
                                  {showTooltip ? (
                                      <TooltipCategories position={'top'} title={x.label}>
                                          <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                                      </TooltipCategories>
                                  ) : (
                                      <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                                  )}
                                  <SidebarButtons>
                                      {x.buttons &&
                                          x.buttons.map((button) => {
                                              return (
                                                  <SidebarButtonStyle
                                                      key={button.key}
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          onSidebarSubItemButtonClick?.(button, x);
                                                      }}
                                                  >
                                                      <TreeSidebarIcon $isActive={button.isActive} tooltip={button.tooltip} $hoverable>
                                                          {button.icon}
                                                      </TreeSidebarIcon>
                                                  </SidebarButtonStyle>
                                              );
                                          })}
                                  </SidebarButtons>
                              </SidebarListItem>
                          );

                          return {
                              id: x?.id,
                              content: categoryNode
                          };
                      });
        const macroNode = (
            <SidebarListItem
                $selected={x.selected}
                disabled={x.disabled}
                //only for 'All Categories'
                onClick={() => onSidebarItemClick(x)}
            >
                {showTooltip ? (
                    <TooltipCategories title={x.label}>
                        <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                    </TooltipCategories>
                ) : (
                    <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                )}
                <SidebarButtons>
                    {x.buttons &&
                        x.buttons.map((button) => {
                            return (
                                <SidebarButtonStyle
                                    key={button.key}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSidebarItemButtonClick?.(button, x);
                                    }}
                                >
                                    <TreeSidebarIcon $isActive={button.isActive} tooltip={button.tooltip} $hoverable>
                                        {button.icon}
                                    </TreeSidebarIcon>
                                </SidebarButtonStyle>
                            );
                        })}
                </SidebarButtons>
            </SidebarListItem>
        );

        return {
            id: x?.id,
            content: macroNode,
            items: subCategories,
            opened: x?.subItems?.some((x) => x?.selected)
        };
    });
    return (
        <SidebarContainer
            $isDialog={isDialog ?? false}
            $isMainSidebar={theme?.additionalProperties.isSidebarHidden ?? false}
            position={'fixed'}
            className={className}
        >
            {sidebarHeader && (
                <SidebarHeaderContainer>
                    {!isSearchMode && (
                        <>
                            <SidebarHeader>{sidebarHeader}</SidebarHeader>
                            <SidebarButtons>
                                {sidebarHeaderButtons &&
                                    sidebarHeaderButtons.map((button) => {
                                        return (
                                            <SidebarButtonStyle key={button.key} onClick={() => onSidebarHeaderButtonClick?.(button)}>
                                                <ZTooltip position={'top'} title={button.tooltip}>
                                                    <Icon $hoverable>{button.icon}</Icon>
                                                </ZTooltip>
                                            </SidebarButtonStyle>
                                        );
                                    })}

                                {sidebarAllowSearch && (
                                    <SidebarButtonStyle key={'search'} onClick={() => setSearchMode(!isSearchMode)}>
                                        <Icon $hoverable>
                                            <SearchIcon />
                                        </Icon>
                                    </SidebarButtonStyle>
                                )}
                            </SidebarButtons>
                        </>
                    )}

                    {isSearchMode && (
                        <>
                            <SidebarSearchInput
                                ref={(instance) => instance?.focus()}
                                containerStyle={{ flex: '1', marginRight: '10px' }}
                                defaultValue={''}
                                onSearch={setFilter}
                                placeholder={T._('Search...', 'Admin')}
                            />

                            <SidebarButtons>
                                <SidebarButtonStyle
                                    onClick={() => {
                                        setSearchMode(false);
                                        setFilter('');
                                    }}
                                >
                                    <Icon $hoverable>
                                        <TimesSolidIcon />
                                    </Icon>
                                </SidebarButtonStyle>
                            </SidebarButtons>
                        </>
                    )}
                </SidebarHeaderContainer>
            )}
            <CollapsibleTreeItems items={macrocategories} />
        </SidebarContainer>
    );
};
